import clsx from "clsx";
import React from "react";
import { FormattedMessage } from "react-intl";
import { Link, useParams } from "react-router-dom";

import { featureFlags } from "~/common/feature-flags";
import { isQueryNotFoundError } from "~/common/query";
import { RemoteLogErrorBoundary } from "~/components/common/remote-log-error-boundary";
import { Button } from "~/components/ui/button";
import { Loader } from "~/components/ui/loader";
import { useIntlDocumentTitle } from "~/hooks/use-document-title";
import type { NumericString } from "~/types/common";

import { DateTimeHeader } from "./book-form";
import { ExpertHeader } from "./book-widget";
import { useGetMeetingQuery } from "./meetings-api";
import { MeetingsEmptyFallback, MeetingsErrorFallback } from "./meetings-shared";
import { useIsRedesignMeetings } from "./redesign-meetings-context";

function getUpcomingMeetingsHref() {
  if (featureFlags.redesignV1 && featureFlags.redesignV1RouteOverride) {
    return "/pages/meetings?tab=upcoming";
  }
  if (featureFlags.redesignV1) {
    return "/pages/redesign/meetings?tab=upcoming";
  }
  return "/pages/meetings/enrolled?tab=upcoming";
}

export function MeetingBookSuccessPage() {
  useIntlDocumentTitle("meetings.book.success.title");

  const { meeting_id } = useParams<{ meeting_id: NumericString }>();
  const meetingQuery = useGetMeetingQuery({ meetingId: meeting_id });

  return (
    <RemoteLogErrorBoundary component="meeting_book_success_page" fallbackRender={MeetingsErrorFallback} onReset={meetingQuery.refetch}>
      <MeetingBookSuccessContent />
    </RemoteLogErrorBoundary>
  );
}

function MeetingBookSuccessContent() {
  const { meeting_id } = useParams<{ meeting_id: NumericString }>();
  const meetingQuery = useGetMeetingQuery({ meetingId: meeting_id });
  const redesign = useIsRedesignMeetings();

  // The redesign wrapper supplies the card surface; the standalone legacy route supplies its own.
  const className = redesign ? "" : "tw-rounded-3xl tw-bg-white tw-py-4 tw-shadow-sm sm:tw-px-12";

  if (meetingQuery.isUninitialized || meetingQuery.isLoading) {
    return (
      <div className={className}>
        <div className="tw-mx-auto tw-grid tw-min-h-[570px] tw-max-w-[700px] tw-place-items-center tw-bg-white">
          <Loader />
        </div>
      </div>
    );
  }

  if (isQueryNotFoundError(meetingQuery.error)) {
    return (
      <MeetingsEmptyFallback title="meetings.detail.not_found" description="meetings.detail.not_found.description" link={{ to: "/pages/meetings/enrolled", children: "meetings.enrolled.title.all" }} />
    );
  }

  if (meetingQuery.isError) {
    return <MeetingsErrorFallback title="meetings.detail.error" resetErrorBoundary={meetingQuery.refetch} />;
  }

  const meeting = meetingQuery.data;

  return (
    <div className={className}>
      <h3
        className={clsx(
          "tw-m-0 tw-border-b tw-pb-5 tw-pt-3 tw-text-center",
          redesign ? "tw-border-border" : "tw-border-[#f1f1f1]",
          "tw-font-rd-accent tw-text-2xl tw-leading-8 tw-text-foreground",
        )}
      >
        <FormattedMessage id="meetings.book.success.heading" values={{ br: <br /> }} />
      </h3>

      <ExpertHeader expert={meeting.expert} />
      <DateTimeHeader time={meeting.start_time_utc} showTimeZone />

      <div className="tw-mt-5 tw-flex tw-justify-center tw-pb-3">
        <Button variant="primary" rounded={redesign || undefined} asChild>
          <Link to={getUpcomingMeetingsHref()}>
            <FormattedMessage id="meetings.book.success.upcoming" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
